import type { InteroperabilityConfigReturn } from "../components/InteroperabilityConfig";

export const CONTEXT_GLOBAL_TYPES = new Set([
  "HOLDER",
  "EXECUTOR",
  "IMPERSONATING",
]);

// Migration map: old variable output_name → { global_type, semantic id }
export const LEGACY_CONTEXT_VARIABLE_MAP: Record<
  string,
  { global_type: "HOLDER" | "EXECUTOR" | "IMPERSONATING"; id: string }
> = {
  "representante-correo": { global_type: "EXECUTOR", id: "email" },
  "representante-telefono": { global_type: "EXECUTOR", id: "phone" },
  "representante-nombres": { global_type: "EXECUTOR", id: "name" },
  "representante-apellidos": { global_type: "EXECUTOR", id: "last_name" },
  "representante-documento-identidad": {
    global_type: "EXECUTOR",
    id: "document_number",
  },
  "holder-correo": { global_type: "HOLDER", id: "email" },
  "holder-telefono": { global_type: "HOLDER", id: "phone" },
  "holder-personal-nombres": { global_type: "HOLDER", id: "name" },
  "holder-personal-apellidos": { global_type: "HOLDER", id: "last_name" },
  "holder-personal-dui": { global_type: "HOLDER", id: "document_number" },
  "holder-legal-nombre-comercial": {
    global_type: "HOLDER",
    id: "trade_name",
  },
  "holder-legal-razon-social": { global_type: "HOLDER", id: "company_name" },
  "holder-legal-nit": { global_type: "HOLDER", id: "document_number" },
  "holder-legal-verificacion": { global_type: "HOLDER", id: "verification" },
  "holder-legal-id-expediente-verificacion": {
    global_type: "HOLDER",
    id: "verification_expedient_id",
  },
  "holder-legal-fecha-de-verificacion": {
    global_type: "HOLDER",
    id: "verification_date",
  },
  "impersonation-impersonacion": {
    global_type: "IMPERSONATING",
    id: "impersonation",
  },
};

export const VARIABLE_NAME_TO_API_VALUE: Record<string, string> = {
  nombres: "first_name",
  apellidos: "last_name",
  "nombre-completo": "name",
  correo: "email",
  telefono: "phone",
  teléfono: "phone",
  "documento-identidad": "document_number",
  "tipo-persona": "type",
  "nombre-comercial": "commercial_name",
  "fecha-nacimiento": "birth_date",
  representación: "impersonation",
  representacion: "impersonation",
  // Legacy names, kept so already-saved flows still resolve.
  impersonación: "impersonation",
  impersonacion: "impersonation",
};

export const DATE_TYPE_WITH_TIME: Record<string, string> = {
  date: "datetime",
  date_range: "datetime_range",
};

export const DATE_TYPE_WITHOUT_TIME: Record<string, string> = {
  datetime: "date",
  datetime_range: "date_range",
};

export type FieldCondition = {
  field_reference?: string;
  field_reference_type?: string;
  condition_type?: string;
  operator?: string;
  value?: string | number | Date | null | unknown[];
};

export type Config = {
  // comunes
  min_length?: number | null;
  max_length?: number | null;
  regex?: string;
  input_type?: string;
  min?: number | null;
  max?: number | null;
  step?: number;
  decimal_places?: number;
  mask?: string;

  //number
  allow_negative?: boolean;
  allow_decimals?: boolean;
  precision?: number | null;
  number_format?: "normal" | "percentage" | "usd" | "eur" | "jpy" | "btc";

  // textarea
  rows?: number;
  cols?: number;
  allow_html?: boolean;
  resize?: boolean;

  // email
  allowed_domains?: string[] | null;
  blocked_domains?: string[] | null;
  require_confirmation?: boolean;

  // select & multiselect
  options?: { value: string; label: string }[];
  data_center_node?: string | null;
  data_center_node_id?: string | null;
  data_center_table?: string | null;
  data_center_column?: string | null;
  data_center_value_type?: string | null;
  source?: string | null; // data base source
  is_var_by_registry?: boolean;
  allow_empty?: boolean;
  searchable?: boolean;
  options_type?: string;

  // multiselect extra
  min_selections?: number;
  max_selections?: number | null;
  allow_duplicates?: boolean;

  // date
  min_date?: string | null;
  max_date?: string | null;
  format?: string;
  disable_weekends?: boolean;
  disable_holidays?: boolean;
  disabled_dates?: string[] | null;
  includeTime?: boolean;

  is_relative_min?: boolean;
  relative_min_value?: number;
  relative_min_unit?: string;
  relative_min_business_days?: boolean;

  is_relative_max?: boolean;
  relative_max_value?: number;
  relative_max_unit?: string;
  relative_max_business_days?: boolean;

  // datetime
  min_datetime?: string | null;
  max_datetime?: string | null;
  time_format?: "12h" | "24h";
  minute_step?: number;

  // time
  min_time?: string | null;
  max_time?: string | null;

  // file
  allowed_mimes?: string[];
  multiple?: boolean;
  accept_folders?: boolean;
  preview_enabled?: boolean;
  max_file_size_mb?: string;
  max_total_size_mb?: string;

  // phone
  country_code?: string;
  allow_extensions?: boolean;
  maxLength?: number;
  allowed_countries?: string[] | null;

  // identification
  document_type?: string;
  format_mode?: "strict" | "loose";
  auto_format?: boolean;
  validate_check_digit?: boolean;
  allow_select_document_type?: boolean;
  isNitHomologado?: boolean;
  linked_variable?: {
    type: "variable" | "interoperability";
    variable?: string;
    node_id?: string;
    field_id?: string;
    output_name?: string;
    field?: string;
    response_key?: string;
    key_name?: string;
  };

  // checkbox (switch)
  label?: string;
  // true_value?: boolean;
  // false_value?: boolean;
  required_value?: boolean;

  // interoperabilidad
  service?: string;
  action?: string;
  input_field?: {
    name: string;
    type: string;
    label?: string;
    placeholder?: string;
    pattern?: string;
  }[];
  output_values?: InteroperabilityConfigReturn[];

  // year
  year_range_type?: "fixed" | "relative";
  min_year?: number | "current" | null;
  max_year?: number | "current" | null;
  year_offset_min?: number | null;
  year_offset_max?: number | null;

  // relative date limits (for full date precision)
  min_relative_offset?: {
    value: number;
    unit: "days" | "months" | "years";
    direction: "before" | "after";
  } | null;
  max_relative_offset?: {
    value: number;
    unit: "days" | "months" | "years";
    direction: "before" | "after";
  } | null;
  // expedient
  expedient_base_entity_id?: string | null;
  additional_columns?: Array<{
    field_id: string;
    search_enabled: boolean;
  }>;
  confirm_reselection?: boolean;
  attach_to_formality_request?: boolean;
  levels?: Array<{
    table?: string;
    display_column?: string;
    [key: string]: unknown;
  }>;
};

export type BaseField = {
  name?: string;
  label?: string;
  id?: string | null;
  type?: string;
  placeholder?: string | null;
  required?: boolean;
  order?: number;
  default_value?: Record<string, unknown> | string | number | boolean | null;
  conditions?: FieldCondition[];
  config: Config;
  is_conditional?: boolean;
  is_read_only?: boolean;
  minValue?: number;
  maxValue?: number;
  is_preventable?: boolean;
  is_array?: boolean;
  max_items?: number | null;
};

export type Conditions = {
  id: number | string;
  data: {
    selectedValue: {
      id?: string;
      name?: string;
      type?: string;
      node_id?: string;
      output_name?: string;
    };
    selectedOperation: {
      name: string;
      label: string;
      needsComparativeValue: boolean;
      comparativeValueType: string;
    };
    compareValueType: string;
    compareInput: string;
    compareDate: string | Date;
    selectedVar: {
      id: string;
      name: string;
      type: string;
    };
  };
};

export type FormattedForm = {
  name: string;
  target_user_type?: string;
  id?: string | null;
  description: string;
  status: "published" | "draft" | "archived";
  steps: Step[];
};

export type Step = {
  name: string;
  id?: string | null;
  description: string;
  order: number;
  sections: Section[];
};

export type Section = {
  title: string;
  id?: string | null;
  description: string;
  order: number;
  fields: BaseField[];
};

export const ROBUST_DATE_FORMATS = [
  "DD/MM/YYYY",
  "DD/MM/YYYY HH:mm",
  "DD/MM/YYYY hh:mm A",
  "YYYY-MM-DD",
  "YYYY-MM-DD HH:mm",
  "YYYY-MM-DD hh:mm A",
];
